export interface TopicTrendPoint {
  label: string;
  value: number;
}

export interface TopicTimeline {
  time: string;
  title: string;
  text: string;
}

export interface TopicOpinion {
  stance: string;
  tone: "agree" | "neutral" | "against";
  pct: number;
  points: string[];
}

export interface Topic {
  id: number;
  title: string;
  categoryName: string;
  categorySlug: string;
  heat: number;
  heatLabel: "爆" | "热" | "发酵中";
  excerpt: string;
  tags: string[];
  date: string;
  reads: number;
  comments: number;
  cover: string;
  trend: TopicTrendPoint[];
  timeline: TopicTimeline[];
  opinions: TopicOpinion[];
  body: string[];
}

export interface Category {
  name: string;
  slug: string;
  icon: string;
}

export const categories: Category[] = [
  { name: "全部", slug: "all", icon: "ri-grid-fill" },
  { name: "社会观察", slug: "society", icon: "ri-earth-line" },
  { name: "科技圈", slug: "tech", icon: "ri-cpu-line" },
  { name: "职场话题", slug: "workplace", icon: "ri-briefcase-line" },
  { name: "娱乐现场", slug: "entertainment", icon: "ri-mic-line" },
  { name: "民生热议", slug: "livelihood", icon: "ri-home-smile-line" },
  { name: "文化现象", slug: "culture", icon: "ri-quill-pen-line" },
];

export const topics: Topic[] = [
  {
    id: 1,
    title: "城市绿道之争：骑行友好该给谁让路？",
    categoryName: "社会观察",
    categorySlug: "society",
    heat: 9864,
    heatLabel: "爆",
    excerpt:
      "一条城市环线绿道爆火后，跑步人群与骑行人群围绕路权吵成一片。绿道到底该偏向谁？背后折射的是城市公共空间如何在多元需求中寻找平衡。",
    tags: ["城市治理", "公共空间", "路权", "骑行文化"],
    date: "今天 09:20",
    reads: 128640,
    comments: 3218,
    cover:
      "https://readdy.ai/api/search-image?query=City%20greenway%20at%20golden%20hour%20with%20cyclists%20and%20runners%20sharing%20a%20tree-lined%20path%2C%20soft%20warm%20sunlight%2C%20editorial%20documentary%20photography%2C%20urban%20landscape%2C%20natural%20and%20authentic&width=1200&height=800&seq=topic-society-1&orientation=landscape",
    trend: [
      { label: "10:00", value: 42 },
      { label: "12:00", value: 58 },
      { label: "14:00", value: 51 },
      { label: "16:00", value: 73 },
      { label: "18:00", value: 88 },
      { label: "20:00", value: 79 },
      { label: "22:00", value: 96 },
    ],
    timeline: [
      {
        time: "昨天 18:40",
        title: "网友晒出绿道晚高峰拥堵视频",
        text: "一条绿道晚高峰时段人车混流的短视频在多个平台扩散，骑行与跑步互不相让的画面引发第一波讨论。",
      },
      {
        time: "今天 07:30",
        title: "本地媒体跟进，双方路权说法各执一词",
        text: "跑步者认为绿道本为慢跑设计，骑行者则强调绿道连通了通勤路线，双方的支持者开始在评论区站队。",
      },
      {
        time: "今天 09:20",
        title: "话题冲上热议榜，发酵进入峰值",
        text: "在多个城市生活博主的转发下，话题热度快速抬升，分时段限行与独立骑道成为交锋焦点。",
      },
      {
        time: "今天 11:05",
        title: "多方呼吁回归城市治理议题本身",
        text: "舆论逐渐从争吵转向理性讨论，不少人提出绿道扩容与精细化管理才是根本解法。",
      },
    ],
    opinions: [
      {
        stance: "支持骑行优先",
        tone: "agree",
        pct: 34,
        points: [
          "绿道本意就是连通慢行通勤，骑行不该被看成抢道。",
          "划出明确骑行道反而更安全，含糊的共享才是乱源。",
          "很多城市已经把骑行纳入通勤体系，这是趋势。",
        ],
      },
      {
        stance: "偏向跑步行人",
        tone: "against",
        pct: 31,
        points: [
          "绿道最初就是给慢跑、散步设计的休闲空间。",
          "骑行速度快，行人安全风险高，不宜混行。",
        ],
      },
      {
        stance: "主张分时段精细管理",
        tone: "neutral",
        pct: 35,
        points: [
          "与其互撕，不如分时段分路段错峰管理。",
          "背后是公共空间资源紧张，扩容才是长久之计。",
          "把讨论拉回城市治理，比站队更有价值。",
        ],
      },
    ],
    body: [
      "一条本意美好的城市绿道，在晚高峰人车相遇的一刻被顶上了风口。短视频里的拥堵画面只是导火索，真正让话题持续发酵的，是它戳中了很多人都体验过的公共空间困境：资源有限，需求多元，谁该被优先满足？",
      "支持骑行的人强调，绿道连接了居住区与工作区，是慢行通勤的重要通道，把骑行排除在外等于让路权倒退；而倾向步行的声音则认为，绿道诞生之初就带着慢的属性，高速穿行的自行车让散步与慢跑的体验和安全都打了折扣。",
      "值得关注的是，双方观点在交锋中逐渐出现交集。越来越多评论开始呼吁从争谁优先转向如何共处，例如分时段限行、预留独立骑道、动态引导人流车流等方案被反复提及。舆论的发酵，正让一个具体的城市问题回归到治理方法本身。",
      "这场讨论的走向，也许比答案更重要。它提醒我们：一座友好的城市，不是替任何一方站队，而是有能力让跑步的、骑行的、散步的人，在同一片绿色里找到各自的节奏。",
    ],
  },
  {
    id: 2,
    title: "AI 换脸视频以假乱真，该由谁来管？",
    categoryName: "科技圈",
    categorySlug: "tech",
    heat: 8730,
    heatLabel: "爆",
    excerpt:
      "一段高度逼真的 AI 换脸视频刷屏网络，真假难辨引发焦虑。技术红利与内容造假之间，监管、平台与个人到底该怎么划界？",
    tags: ["AIGC", "深度伪造", "内容治理", "版权"],
    date: "今天 10:05",
    reads: 98720,
    comments: 2741,
    cover:
      "https://readdy.ai/api/search-image?query=Abstract%20representation%20of%20digital%20face%20transformation%20with%20glowing%20data%20grid%20and%20binary%20code%2C%20warm%20orange%20and%20deep%20ink%20tones%2C%20futuristic%20tech%20concept%2C%20editorial%20illustration&width=1200&height=800&seq=topic-tech-2&orientation=landscape",
    trend: [
      { label: "08:00", value: 30 },
      { label: "10:00", value: 66 },
      { label: "12:00", value: 54 },
      { label: "14:00", value: 82 },
      { label: "16:00", value: 77 },
      { label: "18:00", value: 90 },
      { label: "20:00", value: 85 },
    ],
    timeline: [
      {
        time: "今天 08:30",
        title: "一段真假难辨的换脸视频在社交平台扩散",
        text: "视频因高度拟真迅速被转发，大量网友在评论里表示难以辨认，担忧情绪开始蔓延。",
      },
      {
        time: "今天 10:05",
        title: "技术博主科普辨伪方法，话题热度攀升",
        text: "多位技术从业者下场科普 AI 生成特征与鉴别工具，专业视角让讨论从惊吓走向追问。",
      },
      {
        time: "今天 13:40",
        title: "围绕监管与平台责任的争论进入白热化",
        text: "是否应强制打标、平台审核有无义务、个人权益如何救济成为评论区高频议题。",
      },
    ],
    opinions: [
      {
        stance: "要求强制标识 AI 内容",
        tone: "agree",
        pct: 42,
        points: [
          "生成内容应强制标注来源，让眼见不再为实。",
          "透明标识是低成本高收益的治理抓手。",
          "平台发布时应有不可去除的水印。",
        ],
      },
      {
        stance: "担忧技术被一刀切限制",
        tone: "neutral",
        pct: 27,
        points: [
          "换脸只是应用之一，不应因噎废食。",
          "重点应放在恶意使用而非技术本身。",
        ],
      },
      {
        stance: "强调个人辨伪素养",
        tone: "against",
        pct: 31,
        points: [
          "监管之外，公众也需要基本的辨伪意识。",
          "不能把所有责任都推给平台，个人要学会求证。",
        ],
      },
    ],
    body: [
      "当一段 AI 换脸视频逼真到连本人都要愣一下，它所引发的就不只是好玩，而是一种普遍的信任焦虑。网友最初的震惊，很快被技术从业者的科普带向更深的追问：我们还能相信眼睛看到的吗？",
      "支持者把矛头指向标识透明，认为平台和生成方必须为 AI 内容打上不可磨灭的印记，让传播链条上的每个人都知情。而持保留态度的人则提醒，换脸只是生成式 AI 众多能力之一，治理的重点应该落在恶意使用而非技术本身。",
      "在评论区的交锋里，第三种声音也在壮大：与其期待事后追责，不如把一部分责任交给公众，学会核对来源、交叉验证，是数字时代的基本素养。监管、平台与个人，几乎被讨论塑造成了三角责任的共同体。",
      "AI 的进步不会停步，关于边界在哪的讨论也会持续下去。或许真正的答案，正是在这种一轮轮的追问与共识中慢慢浮现。",
    ],
  },
  {
    id: 3,
    title: "下班即失联是自由还是逃避？年轻人开始反内卷",
    categoryName: "职场话题",
    categorySlug: "workplace",
    heat: 7610,
    heatLabel: "热",
    excerpt:
      "越来越多年轻人选择到点就下班、下班就失联，被戏称为新型职场生存法则。这到底是边界意识的觉醒，还是对责任的逃避？",
    tags: ["职场边界", "反内卷", "工作生活平衡", "Z世代"],
    date: "昨天 21:15",
    reads: 75480,
    comments: 1983,
    cover:
      "https://readdy.ai/api/search-image?query=Young%20professional%20leaving%20office%20at%20dusk%20walking%20toward%20sunset%20while%20reading%20a%20book%2C%20silhouette%20against%20warm%20orange%20sky%2C%20work%20life%20balance%20concept%2C%20editorial%20lifestyle%20photography&width=1200&height=800&seq=topic-workplace-3&orientation=landscape",
    trend: [
      { label: "20:00", value: 38 },
      { label: "21:00", value: 55 },
      { label: "22:00", value: 61 },
      { label: "23:00", value: 76 },
      { label: "24:00", value: 69 },
      { label: "次日08:00", value: 80 },
    ],
    timeline: [
      {
        time: "昨天 20:30",
        title: "一张下班即静音的朋友圈截图被热转",
        text: "有网友晒出到点退出工作群、关闭提醒的设置截图，声称这是自己的新常态，引发大量共鸣。",
      },
      {
        time: "昨天 21:15",
        title: "反内卷与边界感成为讨论关键词",
        text: "评论区出现明显分化：一派点赞这种自我守护，一派担心影响团队协作与个人发展。",
      },
      {
        time: "今天 09:10",
        title: "职场博主接力讨论，话题热度再度走高",
        text: "不少职场类内容创作者结合自身经历参与讨论，让话题从吐槽上升到对职场文化的反思。",
      },
    ],
    opinions: [
      {
        stance: "支持设边界，反对无意义加班",
        tone: "agree",
        pct: 46,
        points: [
          "用加班换来的勤奋未必等于价值，健康更重要。",
          "边界清晰反而提升效率，避免情绪内耗。",
          "下班后的生活不该被工作消息侵占。",
        ],
      },
      {
        stance: "认为需要平衡与主动沟通",
        tone: "neutral",
        pct: 30,
        points: [
          "彻底失联可能影响突发事务，约定紧急通道更稳妥。",
          "关键是质量而非形式，关键项目该上时也要顶上。",
        ],
      },
      {
        stance: "担忧变成消极对抗",
        tone: "against",
        pct: 24,
        points: [
          "如果只是消极不配合，反而伤害职业口碑。",
          "真正的边界要靠专业沟通来争取。",
        ],
      },
    ],
    body: [
      "到点下班、下班失联正在成为一批年轻人公开宣称的职场信条。它不再是一种羞于启齿的躺平，而被赋予边界意识、自我守护的光环。舆论的分裂由此而生。",
      "支持者把它看作对无意义内卷的有力回应：当加班不再对应成长与回报，拒绝用健康换取表演式勤奋，是一种清醒的选择。更多人强调，真正的专业不是随叫随到，而是对结果负责。",
      "而反对与中立的声音也并非毫无道理。他们提醒，职场边界应当通过清晰的沟通来划定，而不是用失联做消极表态；遇到关键任务时，负责任的人会选择主动把话说在前面。",
      "这场讨论看似在争下不下班，实则是在重新定义什么是值得投入的工作。年轻人的反内卷，与其说是逃避责任，不如说是开始认真地追问：努力，到底该流向哪里。",
    ],
  },
  {
    id: 4,
    title: "一档慢综艺为何突然出圈？素人真香定律",
    categoryName: "娱乐现场",
    categorySlug: "entertainment",
    heat: 6980,
    heatLabel: "热",
    excerpt:
      "没有流量明星、没有冲突剧本，一档主打慢与真的素人综艺却意外跑出圈。观众用脚投票的背后，藏着怎样的观看需求转变？",
    tags: ["综艺", "慢生活", "素人节目", "口碑"],
    date: "今天 08:40",
    reads: 66930,
    comments: 1540,
    cover:
      "https://readdy.ai/api/search-image?query=Cozy%20outdoor%20gathering%20of%20ordinary%20people%20around%20a%20wooden%20table%20in%20a%20sunny%20garden%2C%20warm%20afternoon%20light%2C%20documentary%20slow%20tv%20aesthetic%2C%20relaxed%20and%20authentic&width=1200&height=800&seq=topic-ent-4&orientation=landscape",
    trend: [
      { label: "06:00", value: 35 },
      { label: "08:00", value: 62 },
      { label: "10:00", value: 58 },
      { label: "12:00", value: 74 },
      { label: "14:00", value: 81 },
      { label: "16:00", value: 76 },
      { label: "18:00", value: 70 },
    ],
    timeline: [
      {
        time: "今天 06:20",
        title: "一段节目片段被网友自发搬运",
        text: "节目里一段自然、不油腻的素人互动被剪成短视频热传，弹幕里刷满了真实与治愈的评价。",
      },
      {
        time: "今天 08:40",
        title: "去剧本化与真香成为传播标签",
        text: "观众评价集中在真实、治愈两个关键词，相关话题在多个平台同步上升。",
      },
      {
        time: "今天 15:10",
        title: "业内解读慢综艺为何回潮",
        text: "媒体与影评人加入讨论，把节目热度连接到观众对快节奏内容的审美疲劳。",
      },
    ],
    opinions: [
      {
        stance: "爱它的真实与松弛",
        tone: "agree",
        pct: 44,
        points: [
          "看够了剧本和冲突，就想看点正常人的生活。",
          "素人之间的化学反应比硬凹的人设更耐看。",
          "慢下来本身就是一种治愈。",
        ],
      },
      {
        stance: "认为是营销与偶然而已",
        tone: "against",
        pct: 22,
        points: [
          "出圈有运气成分，未必能复制。",
          "所谓无剧本本身就是一种包装。",
        ],
      },
      {
        stance: "看作内容审美的风向标",
        tone: "neutral",
        pct: 34,
        points: [
          "这说明观众审美在升级，行业该重新理解内容。",
          "真实不等于粗糙，用心才是关键。",
        ],
      },
    ],
    body: [
      "没有顶流明星，没有刻意制造的冲突，一档慢综艺却在这个周期里跑成了热议焦点。网友的惊讶与喜欢几乎同时出现：原来没有剧本，反而更能留住人。",
      "节目里的素人没有职业包袱，聊天、劳作、发呆都显得自然松弛。那些在快节奏内容里越来越稀缺的真实感，成了它最锋利的记忆点。弹幕里高频出现的真香，其实是对千篇一律叙事的一种集体反弹。",
      "当然也有声音保持冷静：出圈可能只是阶段性的现象，真实与质感之间仍隔着用心的剪辑与制作。可即便如此，观众在用脚投票这件事上从不含糊。",
      "如果说这档节目提供了什么信号，那大概是：当套路被看穿，真诚反而成为最稀缺的竞争力。慢综艺的回潮，本质是观众对自己注意力的一次郑重选择。",
    ],
  },
  {
    id: 5,
    title: "小城咖啡馆反向走红：一杯咖啡的距离",
    categoryName: "民生热议",
    categorySlug: "livelihood",
    heat: 5340,
    heatLabel: "发酵中",
    excerpt:
      "开在县城街角、价格亲民、装修质朴的社区咖啡店，近来在小红书与短视频里被反复安利。年轻人为何开始为小城咖啡买单？",
    tags: ["县域消费", "社区咖啡", "返乡创业", "生活方式"],
    date: "昨天 19:30",
    reads: 48210,
    comments: 1108,
    cover:
      "https://readdy.ai/api/search-image?query=Charming%20independent%20coffee%20shop%20in%20a%20small%20county%20town%20street%2C%20warm%20morning%20light%20through%20window%2C%20affordable%20simple%20cozy%20decor%2C%20people%20chatting%2C%20documentary%20lifestyle%20photography&width=1200&height=800&seq=topic-livelihood-5&orientation=landscape",
    trend: [
      { label: "10:00", value: 28 },
      { label: "12:00", value: 40 },
      { label: "14:00", value: 38 },
      { label: "16:00", value: 53 },
      { label: "18:00", value: 47 },
      { label: "20:00", value: 60 },
    ],
    timeline: [
      {
        time: "昨天 14:00",
        title: "博主记录返乡开咖啡店的日常走红",
        text: "一段记录县城咖啡店从装修到日常营业的 Vlog 被大量转发，把咖啡带回家乡成为新的叙事。",
      },
      {
        time: "昨天 19:30",
        title: "县城咖啡与反向消费引发共鸣",
        text: "相比一线城市的精致溢价，亲民价格与社区温度让小城咖啡成为讨论焦点。",
      },
      {
        time: "今天 10:20",
        title: "围绕县域消费潜力的讨论升温",
        text: "话题从一家小店延伸到返乡创业、县域商业等更宏观的观察。",
      },
    ],
    opinions: [
      {
        stance: "为小而美的社区感点赞",
        tone: "agree",
        pct: 38,
        points: [
          "价格实在、老板是邻居，比写字楼咖啡更有人情味。",
          "小城慢生活配上咖啡，是理想的返乡样本。",
        ],
      },
      {
        stance: "担心只是一阵风",
        tone: "against",
        pct: 26,
        points: [
          "县域人口与消费力有限，很难养活太多精品店。",
          "很多店可能热闹一阵就关了。",
        ],
      },
      {
        stance: "看作县域消费升级的信号",
        tone: "neutral",
        pct: 36,
        points: [
          "这说明年轻人正在把生活方式带回去，是好事。",
          "关键在于可持续的生意逻辑，而非流量故事。",
        ],
      },
    ],
    body: [
      "一杯原本属于一线城市写字楼的咖啡，正在悄悄改变小城的街角。近来一批价格亲民、装修质朴、老板亲手冲煮的县域咖啡店被反复安利，反向走红的背后，是年轻人生活方式的下沉与回流。",
      "打动网友的往往不是咖啡本身，而是那种触手可及的松弛感：不用为环境溢价买单，老板能记住你的口味，街坊邻里把这当成日常碰头的地方。这和大城市里买一杯就走的快节奏形成鲜明对比。",
      "另一种声音同样值得听：县域市场的人口与消费力决定了它很难无限复制这种小而美。比起流量的热闹，大家更关心小店能否跑通长期经营的账本。",
      "这场讨论真正关心的，或许是一座城市能否给年轻人留下可经营的生活。小城咖啡被围观，是因为它用一杯咖啡的距离，装下了许多人对返乡与理想生活的想象。",
    ],
  },
  {
    id: 6,
    title: "电子榨菜成下饭标配，短视频正在重塑口味",
    categoryName: "文化现象",
    categorySlug: "culture",
    heat: 4210,
    heatLabel: "发酵中",
    excerpt:
      "吃饭必配一段电子榨菜，成为越来越多人的日常仪式。短视频究竟在喂养我们，还是在重新定义我们的观看与陪伴习惯？",
    tags: ["短视频", "媒介消费", "陪伴经济", "内容生态"],
    date: "昨天 22:10",
    reads: 38540,
    comments: 896,
    cover:
      "https://readdy.ai/api/search-image?query=Person%20enjoying%20a%20meal%20while%20watching%20a%20bright%20phone%20screen%20propped%20at%20the%20table%2C%20warm%20cozy%20lighting%2C%20short%20video%20content%20culture%20concept%2C%20editorial%20photography%20with%20soft%20focus&width=1200&height=800&seq=topic-culture-6&orientation=landscape",
    trend: [
      { label: "12:00", value: 45 },
      { label: "14:00", value: 39 },
      { label: "18:00", value: 58 },
      { label: "19:00", value: 71 },
      { label: "21:00", value: 63 },
      { label: "23:00", value: 52 },
    ],
    timeline: [
      {
        time: "昨天 20:40",
        title: "网友吐槽没段子吃不下饭引发共鸣",
        text: "一条关于吃饭一定要配电子榨菜的动态被大量点赞，评论区纷纷分享各自的下饭清单。",
      },
      {
        time: "昨天 22:10",
        title: "电子榨菜成为讨论热词",
        text: "媒体评论开始分析这一习惯背后的陪伴需求与注意分配。",
      },
      {
        time: "今天 09:00",
        title: "话题延伸至内容生态与注意力讨论",
        text: "讨论从个人习惯上升到短视频对时间与审美的影响。",
      },
    ],
    opinions: [
      {
        stance: "认同电子榨菜的陪伴价值",
        tone: "agree",
        pct: 40,
        points: [
          "一个人吃饭真的很需要一点背景声和烟火气。",
          "好的内容就是最好的佐餐。",
        ],
      },
      {
        stance: "警惕注意力被切碎",
        tone: "against",
        pct: 27,
        points: [
          "边吃边刷往往食不知味，时间也悄悄溜走。",
          "短视频的算法推荐让人很难停下来。",
        ],
      },
      {
        stance: "主张适度与选择",
        tone: "neutral",
        pct: 33,
        points: [
          "把它当工具而不是依赖，关键在于自我控制。",
          "值得反思的是内容质量而非观看本身。",
        ],
      },
    ],
    body: [
      "吃饭必配电子榨菜，这句略带自嘲的话正在成为许多人的真实日常。一段短视频、一场直播或一集追更，构成了一个人的餐桌背景。媒介，正在以一种意想不到的方式参与我们的味觉体验。",
      "对于独居与单身人群而言，电子榨菜提供的与其说是内容，不如说是一种陪伴的温度，它让安静的饭桌不再显得空旷。从这个角度看，它承载的是现代人真实的社交与情感需求。",
      "但质疑同样成立：当注意力被切成几十秒的碎片，我们是否还能完整地吃一顿饭、看一个故事？算法推荐让只看一会常常失控成刷到深夜，这的确是值得警惕的副作用。",
      "也许电子榨菜本身无对错，关键在于它是被我们享用，还是反过来操控了我们。如何在陪伴与专注之间找到刻度，是这个时代交给每个干饭人的课题。",
    ],
  },
];

export const hotRank = [...topics].sort((a, b) => b.heat - a.heat);